// frontend/src/components/staff/staffScheduleUtils.js
//
// Workforce — SCHED-SLICE1-G staff schedule view helpers.
//
// Pure functions for rendering the staff-side read-only `/staff/my-schedule`
// surface. Loosely coupled to the `Shift` schema (locked in
// docs/architecture/workforce-scheduling-tech.md §"Shift — promote from
// Phase B to Phase A for Slice 1" + data-models §17.3) consumed via
// `GET /api/workforce/my-shifts`:
//
//   Shift shape (Slice 1 staff-view subset — own shifts only, status=published):
//     _id, staffProfileId, startTime (ISO), endTime (ISO),
//     roleCode, status, notes?, weekStartDate, updatedAt?
//
// All timestamps render in America/Phoenix per CLAUDE.md global rule.
// All times labeled "MST" explicitly.
//
// Pay-rate, labor-cost, and other-staff information is NEVER read here —
// the BE redacts per §17.1.1 RBAC (staff can't see own rate in this lane).

import moment from "moment-timezone";

export const STUDIO_TZ = "America/Phoenix";
export const STUDIO_TZ_LABEL = "MST";

/**
 * Role-color vocabulary — mirrors the admin builder per visual spec §3.2 +
 * §9.2 cross-surface consistency requirement. Slice 1 ships three role
 * codes; "other" is the fallback for forward-compat.
 *
 * NOTE: must stay aligned with the admin Schedule Builder (SCHED-1E) so
 * scenario 13 (cross-surface consistency) holds.
 */
export const ROLE_COLOR_MAP = Object.freeze({
  front_desk: {
    key: "front_desk",
    label: "Front Desk",
    bg: "#2F855A",        // status-clocked-in green
    text: "#ffffff",
  },
  massage_therapist: {
    key: "massage_therapist",
    label: "Massage Therapist",
    bg: "#1f5c63",        // brand-teal
    text: "#ffffff",
  },
  other: {
    key: "other",
    label: "Other",
    bg: "#553C9A",        // status-locked purple
    text: "#ffffff",
  },
});

/**
 * Lookup the role-color descriptor for a given role code. Falls back to
 * "other" purple for unknown codes (forward-compat).
 */
export const getRoleColor = (roleCode) =>
  ROLE_COLOR_MAP[roleCode] || ROLE_COLOR_MAP.other;

/**
 * Format a role code for human display. Falls back to a humanized version
 * of the raw code for unknown codes (forward-compat).
 */
export const formatRoleLabel = (roleCode) => {
  if (!roleCode) return "Shift";
  const known = ROLE_COLOR_MAP[roleCode];
  if (known) return known.label;
  // Forward-compat: turn "shift_lead" -> "Shift Lead"
  return String(roleCode)
    .split("_")
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ""))
    .join(" ")
    .trim();
};

/**
 * Compute the Phoenix-MST week-start (Sunday) for a given reference date.
 * Returns a YYYY-MM-DD string.
 *
 * @param {Date|string|moment.Moment} [ref] - defaults to "now" in Phoenix
 */
export const computeWeekStart = (ref) => {
  const m = ref ? moment.tz(ref, STUDIO_TZ) : moment.tz(STUDIO_TZ);
  if (!m.isValid()) {
    return moment.tz(STUDIO_TZ).startOf("week").format("YYYY-MM-DD");
  }
  return m.startOf("week").format("YYYY-MM-DD");
};

/**
 * Given a weekStart (YYYY-MM-DD or ISO), return the {from, to} ISO strings
 * for an inclusive 7-day window. Used as `GET /api/workforce/my-shifts`
 * params per tech doc §"Staff endpoints (view-as-staff)".
 */
export const computeWeekRange = (weekStart) => {
  const start = moment.tz(weekStart, STUDIO_TZ).startOf("day");
  const end = start.clone().add(7, "days").subtract(1, "second");
  return { from: start.toISOString(), to: end.toISOString() };
};

/**
 * Step a weekStart by N weeks (positive forward, negative backward).
 * Returns a YYYY-MM-DD string.
 */
export const stepWeek = (weekStart, deltaWeeks) => {
  const m = moment.tz(weekStart, STUDIO_TZ).startOf("week");
  return m.add(deltaWeeks, "weeks").format("YYYY-MM-DD");
};

/**
 * Format the H1 week range label. Example: "Apr 21 - Apr 27, 2026".
 */
export const formatWeekRangeLabel = (weekStart) => {
  if (!weekStart) return "";
  const start = moment.tz(weekStart, STUDIO_TZ);
  if (!start.isValid()) return "";
  const end = start.clone().add(6, "days");
  if (start.year() === end.year()) {
    return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
  }
  return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
};

/**
 * Build the 7 day-column descriptors for a given weekStart.
 * Each entry: { date: YYYY-MM-DD, dayName: 'Mon', dayLabel: 'Apr 21',
 * dayNumber: 21, isToday: boolean, isPast: boolean (whole day in past) }.
 */
export const buildWeekDays = (weekStart, nowRef) => {
  const now = nowRef ? moment.tz(nowRef, STUDIO_TZ) : moment.tz(STUDIO_TZ);
  const start = moment.tz(weekStart, STUDIO_TZ).startOf("week");
  const todayKey = now.format("YYYY-MM-DD");
  return Array.from({ length: 7 }, (_, i) => {
    const d = start.clone().add(i, "days");
    return {
      date: d.format("YYYY-MM-DD"),
      dayName: d.format("ddd"),
      dayLabel: d.format("MMM D"),
      dayNumber: d.date(),
      monthAbbr: d.format("MMM"),
      isToday: d.format("YYYY-MM-DD") === todayKey,
      isPast: d.isBefore(now.clone().startOf("day")),
    };
  });
};

/**
 * Format a single shift block label. Example: "8:00am - 2:00pm MST".
 */
export const formatShiftTimeRange = (startIso, endIso) => {
  const s = moment.tz(startIso, STUDIO_TZ);
  const e = moment.tz(endIso, STUDIO_TZ);
  if (!s.isValid() || !e.isValid()) return "";
  return `${s.format("h:mma")} - ${e.format("h:mma")} ${STUDIO_TZ_LABEL}`;
};

/**
 * Format a shift detail modal date line. Example: "Tue Apr 22, 2026".
 */
export const formatShiftDateLine = (startIso) => {
  const s = moment.tz(startIso, STUDIO_TZ);
  if (!s.isValid()) return "";
  return s.format("ddd MMM D, YYYY");
};

/**
 * Format a single timestamp in MST. Example: "Apr 22 8:00 AM MST".
 */
export const formatShiftStamp = (iso) => {
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "";
  return `${m.format("MMM D h:mm A")} ${STUDIO_TZ_LABEL}`;
};

/**
 * Format a shift duration in hours. Returns a string like "6 hours" or
 * "1.5 hours". Falls back to "—" for invalid input.
 */
export const formatShiftDuration = (startIso, endIso) => {
  const s = moment.tz(startIso, STUDIO_TZ);
  const e = moment.tz(endIso, STUDIO_TZ);
  if (!s.isValid() || !e.isValid()) return "—";
  const mins = e.diff(s, "minutes");
  if (mins <= 0) return "—";
  const hours = mins / 60;
  if (Number.isInteger(hours)) {
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  // One decimal place.
  return `${hours.toFixed(1)} hours`;
};

/**
 * Returns true if a shift's startTime is in the past relative to `now`.
 * Used for the 70% opacity past-shift treatment per visual spec §9.2.
 */
export const isShiftPast = (shift, nowRef) => {
  if (!shift || !shift.startTime) return false;
  const now = nowRef ? moment.tz(nowRef, STUDIO_TZ) : moment.tz(STUDIO_TZ);
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  if (!start.isValid()) return false;
  return start.isBefore(now);
};

/**
 * Group an array of shifts into a Map keyed by YYYY-MM-DD (the local
 * Phoenix-MST date of `startTime`). Returns a Map<string, Shift[]> with
 * shifts within each day sorted by startTime ascending.
 */
export const groupShiftsByDay = (shifts) => {
  const map = new Map();
  if (!Array.isArray(shifts)) return map;
  for (const sh of shifts) {
    if (!sh || !sh.startTime) continue;
    const key = moment.tz(sh.startTime, STUDIO_TZ).format("YYYY-MM-DD");
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(sh);
  }
  for (const [k, arr] of map.entries()) {
    arr.sort((a, b) => {
      const ta = moment.tz(a.startTime, STUDIO_TZ).valueOf();
      const tb = moment.tz(b.startTime, STUDIO_TZ).valueOf();
      return ta - tb;
    });
    map.set(k, arr);
  }
  return map;
};

/**
 * Build an accessible aria-label for a shift block per visual spec §17.
 * Example: "Front Desk shift, Tuesday April 22, 8 AM to 2 PM MST".
 */
export const buildShiftAriaLabel = (shift) => {
  if (!shift || !shift.startTime || !shift.endTime) return "Shift";
  const s = moment.tz(shift.startTime, STUDIO_TZ);
  const e = moment.tz(shift.endTime, STUDIO_TZ);
  if (!s.isValid() || !e.isValid()) return "Shift";
  const roleLabel = formatRoleLabel(shift.roleCode);
  return `${roleLabel} shift, ${s.format("dddd MMMM D")}, ${s.format("h:mm A")} to ${e.format("h:mm A")} ${STUDIO_TZ_LABEL}`;
};

/**
 * Pick the next upcoming shift within `windowMinutes` (default 60) of
 * the current time. Returns null if no shift starts within that window
 * OR if a shift has already started (per visual spec §9.4: banner does
 * NOT autoshow if shift already started).
 *
 * @param {Array<Shift>} shifts
 * @param {number} windowMinutes - default 60
 * @param {Date|string} [nowRef] - for testability
 * @returns {Shift | null}
 */
export const pickUpcomingShiftWithinWindow = (shifts, windowMinutes = 60, nowRef) => {
  const now = nowRef ? moment.tz(nowRef, STUDIO_TZ) : moment.tz(STUDIO_TZ);
  if (!Array.isArray(shifts) || shifts.length === 0) return null;
  let best = null;
  let bestDelta = Infinity;
  for (const sh of shifts) {
    if (!sh || !sh.startTime) continue;
    const start = moment.tz(sh.startTime, STUDIO_TZ);
    if (!start.isValid()) continue;
    const deltaMins = start.diff(now, "minutes");
    if (deltaMins < 0) continue;            // already started — skip
    if (deltaMins > windowMinutes) continue; // outside window
    if (deltaMins < bestDelta) {
      best = sh;
      bestDelta = deltaMins;
    }
  }
  return best;
};

/**
 * Compute the "starts in N minutes" countdown for a shift, relative to now.
 * Returns a number of whole minutes (>= 0) or null if invalid.
 */
export const computeMinutesUntilStart = (shift, nowRef) => {
  if (!shift || !shift.startTime) return null;
  const now = nowRef ? moment.tz(nowRef, STUDIO_TZ) : moment.tz(STUDIO_TZ);
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  if (!start.isValid()) return null;
  const mins = start.diff(now, "minutes");
  return mins >= 0 ? mins : null;
};

/**
 * Build a stable change-detection signature for a list of shifts.
 *
 * Two shifts arrays produce the same signature iff every shift has the
 * same _id, startTime, endTime, staffProfileId, roleCode, status, and
 * updatedAt. Used by the polling banner to detect "schedule changed
 * since last view" per visual spec §9.5 + AC Scenario 7.
 *
 * Order-independent — uses sorted _ids.
 */
export const buildShiftsSignature = (shifts) => {
  if (!Array.isArray(shifts) || shifts.length === 0) return "[]";
  const parts = shifts
    .filter((sh) => sh && sh._id)
    .map((sh) => {
      const u = sh.updatedAt || "";
      return [
        sh._id,
        sh.startTime || "",
        sh.endTime || "",
        sh.staffProfileId || "",
        sh.roleCode || "",
        sh.status || "",
        u,
      ].join("|");
    });
  parts.sort();
  return JSON.stringify(parts);
};

/**
 * Count the per-shift differences between two shift arrays — how many
 * shifts in `newShifts` are new or have a changed signature vs the
 * corresponding shift in `oldShifts`. Used to build the "N shift changed"
 * banner copy.
 *
 * NOTE: this counts adds + edits, not removals. Removals also count as
 * 1 change each per visual spec §9.5 ("N shift changed since your last
 * view") so this function counts the symmetric difference.
 */
export const countChangedShifts = (oldShifts, newShifts) => {
  const oldMap = new Map();
  const newMap = new Map();
  if (Array.isArray(oldShifts)) {
    for (const sh of oldShifts) {
      if (sh && sh._id) oldMap.set(String(sh._id), sh);
    }
  }
  if (Array.isArray(newShifts)) {
    for (const sh of newShifts) {
      if (sh && sh._id) newMap.set(String(sh._id), sh);
    }
  }
  let changes = 0;
  for (const [id, sh] of newMap.entries()) {
    const old = oldMap.get(id);
    if (!old) {
      changes += 1;
      continue;
    }
    const oldSig = [old.startTime, old.endTime, old.staffProfileId, old.roleCode, old.status, old.updatedAt || ""].join("|");
    const newSig = [sh.startTime, sh.endTime, sh.staffProfileId, sh.roleCode, sh.status, sh.updatedAt || ""].join("|");
    if (oldSig !== newSig) changes += 1;
  }
  for (const id of oldMap.keys()) {
    if (!newMap.has(id)) changes += 1;
  }
  return changes;
};

/**
 * Format the "Schedule changed" banner copy per visual spec §9.5 with
 * singular/plural correctness.
 *
 * Examples:
 *   "Your schedule was updated — 1 shift changed since your last view. Refresh to see."
 *   "Your schedule was updated — 3 shifts changed since your last view. Refresh to see."
 */
export const formatScheduleChangedBannerText = (n) => {
  const count = Number.isFinite(n) && n > 0 ? n : 0;
  if (count <= 0) return "";
  const noun = count === 1 ? "shift changed" : "shifts changed";
  return `Your schedule was updated — ${count} ${noun} since your last view. Refresh to see.`;
};

/**
 * Format the "Shift starts in N minutes" banner copy per visual spec §9.4.
 * Example: "Your shift starts in 45 minutes".
 *
 * Special cases:
 *   - 1 minute   -> "in 1 minute"
 *   - 0 minutes  -> "now" ("Your shift starts now") — banner still shows
 */
export const formatShiftStartsInText = (minutes) => {
  const n = Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0;
  if (n === 0) return "Your shift starts now";
  if (n === 1) return "Your shift starts in 1 minute";
  return `Your shift starts in ${n} minutes`;
};

/**
 * Format the upcoming-shift banner subtitle per visual spec §9.4.
 * Example: "Front Desk - Tue Apr 22, 2:00pm MST".
 */
export const formatUpcomingShiftSubline = (shift) => {
  if (!shift || !shift.startTime) return "";
  const role = formatRoleLabel(shift.roleCode);
  const s = moment.tz(shift.startTime, STUDIO_TZ);
  if (!s.isValid()) return role || "";
  return `${role} - ${s.format("ddd MMM D, h:mma")} ${STUDIO_TZ_LABEL}`;
};

/**
 * Filter cancelled shifts so only those whose original startTime falls within
 * the currently-visible week render in the "Recently cancelled" section.
 *
 * Per SCHED-CANCELLED-VISUAL-STAFF design §2.6: "Recently" is week-scoped —
 * matches the day-grid's week scope so navigating to a different week shows
 * that week's cancellations only. Backend already returns cancelled shifts
 * within the requested date range, but this filter is the defensive last-mile
 * pass in case the BE returns a wider window.
 *
 * Defensive on non-array input: returns []. Defensive on invalid shifts:
 * skips silently rather than throwing.
 */
export const filterCancelledShiftsForWeek = (cancelledShifts, weekStart) => {
  if (!Array.isArray(cancelledShifts) || cancelledShifts.length === 0) return [];
  if (!weekStart) return [];
  const start = moment.tz(weekStart, STUDIO_TZ).startOf("week");
  if (!start.isValid()) return [];
  const end = start.clone().add(7, "days");
  const out = [];
  for (const sh of cancelledShifts) {
    if (!sh || !sh.startTime) continue;
    const t = moment.tz(sh.startTime, STUDIO_TZ);
    if (!t.isValid()) continue;
    if (t.isSameOrAfter(start) && t.isBefore(end)) {
      out.push(sh);
    }
  }
  // Sort by original startTime ASC so the cancelled list reads
  // chronologically — matches the day-grid mental model.
  out.sort((a, b) => {
    const ta = moment.tz(a.startTime, STUDIO_TZ).valueOf();
    const tb = moment.tz(b.startTime, STUDIO_TZ).valueOf();
    return ta - tb;
  });
  return out;
};

/**
 * Format a cancelled-shift card's line 1: "[day] [date] · [start] - [end] MST · [role]".
 * Example: "Mon Apr 22 · 8:00am - 2:00pm MST · Front Desk"
 *
 * Per visual spec §2.4. Defensive on invalid timestamps — returns empty
 * pieces gracefully without throwing.
 */
export const formatCancelledShiftWhenLine = (shift) => {
  if (!shift || !shift.startTime || !shift.endTime) return "";
  const s = moment.tz(shift.startTime, STUDIO_TZ);
  const e = moment.tz(shift.endTime, STUDIO_TZ);
  if (!s.isValid() || !e.isValid()) return "";
  const whenLabel = s.format("ddd MMM D");
  const timeRange = `${s.format("h:mma")} - ${e.format("h:mma")} ${STUDIO_TZ_LABEL}`;
  const role = formatRoleLabel(shift.roleCode);
  return `${whenLabel} · ${timeRange} · ${role}`;
};

/**
 * Resolve a display name for `cancelledBy` from either a populated user
 * object (`{firstName, lastName, email}`) or a raw ObjectId string.
 *
 * Returns null when the field is null/undefined OR a raw string id (caller
 * should fall back to the generic "admin" copy per spec §5). Mirrors the
 * resolveCancelledByDisplay helper in CancelledShiftDetailModal so admin +
 * staff render the same defensive name resolution.
 */
export const resolveCancelledByName = (cancelledBy) => {
  if (!cancelledBy) return null;
  if (typeof cancelledBy === "string") return null;
  if (typeof cancelledBy === "object") {
    const first = cancelledBy.firstName || "";
    const last = cancelledBy.lastName || "";
    const composed = `${first} ${last}`.trim();
    if (composed) return composed;
    if (cancelledBy.email) return cancelledBy.email;
  }
  return null;
};

/**
 * Format a cancelled-shift card's line 2: "Cancelled [date] by [name]".
 * Examples:
 *   "Cancelled Apr 21 by Roderick"     — when cancelledAt + cancelledBy populated
 *   "Cancelled Apr 21 by admin"        — when cancelledBy is null/raw id (generic fallback)
 *   "Cancelled by Roderick"            — when cancelledAt is null (omit date)
 *   "Cancelled by admin"               — when both cancelledAt and cancelledBy null
 *
 * Per visual spec §5 defensive handling matrix. Tone stays neutral — never
 * alarmist — because the staff section is exception-context, not blame.
 */
export const formatCancelledMetaLine = (shift) => {
  if (!shift) return "";
  const name = resolveCancelledByName(shift.cancelledBy) || "admin";
  const m = shift.cancelledAt ? moment.tz(shift.cancelledAt, STUDIO_TZ) : null;
  if (m && m.isValid()) {
    return `Cancelled ${m.format("MMM D")} by ${name}`;
  }
  return `Cancelled by ${name}`;
};

/**
 * Format an aria-label for a cancelled-shift card per spec §10.
 * Example: "Cancelled shift: Mon Apr 22, 8:00 AM to 2:00 PM Front Desk.
 *           Cancelled on Apr 21 by Roderick. Reason: Staff requested swap."
 *
 * Defensive on missing reason — uses "No reason provided" fallback so screen
 * readers always announce a complete sentence.
 */
export const buildCancelledShiftAriaLabel = (shift) => {
  if (!shift || !shift.startTime || !shift.endTime) return "Cancelled shift";
  const s = moment.tz(shift.startTime, STUDIO_TZ);
  const e = moment.tz(shift.endTime, STUDIO_TZ);
  if (!s.isValid() || !e.isValid()) return "Cancelled shift";
  const role = formatRoleLabel(shift.roleCode);
  const whenStr = `${s.format("ddd MMM D")}, ${s.format("h:mm A")} to ${e.format("h:mm A")} ${role}`;
  const name = resolveCancelledByName(shift.cancelledBy) || "admin";
  const cancelledStamp = shift.cancelledAt
    ? moment.tz(shift.cancelledAt, STUDIO_TZ)
    : null;
  const metaStr = cancelledStamp && cancelledStamp.isValid()
    ? `Cancelled on ${cancelledStamp.format("MMM D")} by ${name}`
    : `Cancelled by ${name}`;
  const reasonRaw =
    typeof shift.cancelReason === "string" ? shift.cancelReason.trim() : "";
  const reasonStr = reasonRaw.length > 0
    ? `Reason: ${reasonRaw}`
    : "Reason: No reason provided";
  return `Cancelled shift: ${whenStr}. ${metaStr}. ${reasonStr}`;
};
